import * as React from 'react';
import { createTheme, ThemeProvider, responsiveFontSizes } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useUserSettings } from '../context/UserSettingsContext';
import { alpha } from '@mui/material/styles';

// Extend the Palette interface to include our custom properties
declare module '@mui/material/styles' {
  interface Palette {
    chart: {
      insideHtc: string;
      outsideHtc: string;
      wall: string;
      insideFouling: string;
      outsideFouling: string;
    };
    tooltip: {
      background: string;
      border: string;
    };
  }
  interface PaletteOptions {
    chart?: {
      insideHtc: string;
      outsideHtc: string;
      wall: string;
      insideFouling: string;
      outsideFouling: string;
    };
    tooltip?: {
      background: string;
      border: string;
    };
  }
}

// Professional engineering theme palettes
const lightPalette = {
  mode: 'light' as PaletteMode,
  primary: {
    main: '#2c3e50',          // professional dark blue-gray
    light: '#34495e',
    dark: '#1a252f',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#00a787',          // corporate teal
    light: '#4FD1C5',
    dark: '#285E61',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#F7FAFC',       // very light gray
    paper: '#FFFFFF',         // white paper
  },
  error: {
    main: '#e0251b',          // corporate red
    light: '#f56565',
    dark: '#c53030',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#ffa300',          // corporate orange
    light: '#f6ad55',
    dark: '#dd6b20',
    contrastText: '#000000',
  },
  info: {
    main: '#0075a8',          // reuse corporate blue as info
    light: '#4299E1',
    dark: '#1A365D',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#6aa539',          // corporate green
    light: '#68d391',
    dark: '#2f855a',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#53555a',       // dark neutral
    secondary: '#63666a',     // medium-dark neutral
    disabled: '#a7a8aa',
  },
  divider: '#c8c9c7',
  chart: {
    insideHtc: '#0075a8',     // corporate blue
    outsideHtc: '#00a787',    // corporate teal
    wall: '#ffa300',          // corporate orange
    insideFouling: '#e0251b', // corporate red
    outsideFouling: '#6aa539', // corporate green
  },
  tooltip: {
    background: '#FFFFFF',
    border: '#c8c9c7',
  },
};


const darkPalette = {
  mode: 'dark' as PaletteMode,
  primary: {
    main: '#4299E1',          // lighter blue for dark mode
    light: '#63B3ED',
    dark: '#2B6CB0',
    contrastText: '#FFFFFF',
  },
  secondary: {
    main: '#4FD1C5',          // lighter teal for dark mode
    light: '#9AE6B4',
    dark: '#38B2AC',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#1A202C',       // dark background
    paper: '#2D3748',         // slightly lighter dark background
  },
  error: {
    main: '#e0251b',          // corporate red
    light: '#f56565',
    dark: '#c53030',
    contrastText: '#FFFFFF',
  },
  warning: {
    main: '#ffa300',         // brand orange
    light: '#f6ad55',
    dark: '#dd6b20',
    contrastText: '#000000',
  },
  info: {
    main: '#0075a8',         // corporate blue
    light: '#4299E1',
    dark: '#1A365D',
    contrastText: '#FFFFFF',
  },
  success: {
    main: '#6aa539',         // brand green
    light: '#68d391',
    dark: '#2f855a',
    contrastText: '#FFFFFF',
  },
  text: {
    primary: '#F7FAFC',       // very light gray for primary text
    secondary: '#E2E8F0',     // light gray for secondary text
    disabled: '#A0AEC0',      // medium gray for disabled text
  },
  divider: '#63666a',
  chart: {
    insideHtc: '#4299E1',    // lighter blue
    outsideHtc: '#81E6D9',   // lighter teal
    wall: '#f6ad55',         // lighter orange
    insideFouling: '#f56565', // lighter red
    outsideFouling: '#68d391', // lighter green
  },
  tooltip: {
    background: '#2D3748',
    border: '#4A5568',
  },
};


// Typography settings - Apple-inspired system fonts
const typography = {
  fontFamily: [
    '-apple-system',
    'BlinkMacSystemFont',
    '"SF Pro Display"',
    '"SF Pro Text"',
    'Inter',
    'system-ui',
    'Roboto',
    '"Helvetica Neue"',
    'Arial',
    'sans-serif'
  ].join(','),
  h1: {
    fontWeight: 700,
    letterSpacing: '-0.02em',
    lineHeight: 1.1,
  },
  h2: {
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.2,
  },
  h3: {
    fontWeight: 600,
    letterSpacing: '-0.01em',
    lineHeight: 1.25,
  },
  h4: {
    fontWeight: 600,
    letterSpacing: '-0.005em',
    lineHeight: 1.3,
  },
  h5: {
    fontWeight: 500,
    letterSpacing: '0em',
    lineHeight: 1.35,
  },
  h6: {
    fontWeight: 500,
    letterSpacing: '0.01em',
    lineHeight: 1.4,
  },
  body1: {
    letterSpacing: '0.01em',
    lineHeight: 1.6,
  },
  body2: {
    letterSpacing: '0.01em',
    lineHeight: 1.5,
  },
};

interface ThemeConfigProps {
  children: React.ReactNode;
}

const ThemeConfig: React.FC<ThemeConfigProps> = ({ children }) => {
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  const { settings } = useUserSettings();

  // Determine the theme mode based on user settings
  const getThemeMode = React.useCallback((): PaletteMode => {
    if (settings.theme === 'system') {
      return prefersDarkMode ? 'dark' : 'light';
    }
    return settings.theme as PaletteMode;
  }, [settings.theme, prefersDarkMode]);  // Create the theme with the appropriate mode
  const theme = React.useMemo(() => {
    const mode = getThemeMode();
    const currentPalette = mode === 'dark' ? darkPalette : lightPalette;
    
    const baseTheme = createTheme({
      palette: currentPalette,
      typography,
    });

    // Apply Apple-inspired component overrides
    return responsiveFontSizes(createTheme(baseTheme, {
      components: {
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: 'none',
              borderRadius: 8,
              padding: '8px 16px',
              fontWeight: 500,
              fontSize: '0.875rem',
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
            },
            contained: {
              boxShadow: mode === 'dark' 
                ? `0px 1px 3px ${alpha('#000000', 0.3)}, 0px 4px 8px ${alpha('#000000', 0.15)}`
                : `0px 1px 3px ${alpha('#000000', 0.12)}, 0px 4px 8px ${alpha('#000000', 0.08)}`,
              '&:hover': {
                transform: 'translateY(-1px)',
                boxShadow: mode === 'dark'
                  ? `0px 2px 8px ${alpha('#000000', 0.4)}, 0px 8px 16px ${alpha('#000000', 0.2)}`
                  : `0px 2px 8px ${alpha('#000000', 0.15)}, 0px 8px 16px ${alpha('#000000', 0.1)}`,
              },
              '&:active': {
                transform: 'translateY(0px)',
              },
            },
            outlined: {
              borderWidth: 1,
              borderColor: currentPalette.divider,
              backdropFilter: 'blur(10px)',
              backgroundColor: alpha(currentPalette.background.paper, 0.5),
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 16,
              border: `1px solid ${alpha(currentPalette.divider, 0.5)}`,
              backdropFilter: 'blur(20px)',
              boxShadow: mode === 'dark'
                ? `0px 4px 16px ${alpha('#000000', 0.3)}, 0px 1px 4px ${alpha('#000000', 0.2)}`
                : `0px 4px 16px ${alpha('#000000', 0.08)}, 0px 1px 4px ${alpha('#000000', 0.04)}`,
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              '& .MuiOutlinedInput-root': {
                borderRadius: 12,
                backgroundColor: alpha(currentPalette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: alpha(currentPalette.background.paper, 0.8),
                },
                '&.Mui-focused': {
                  backgroundColor: alpha(currentPalette.background.paper, 0.9),
                  boxShadow: `0px 0px 0px 3px ${alpha(currentPalette.primary.main, 0.3)}`,
                },
                '& fieldset': {
                  borderColor: alpha(currentPalette.divider, 0.6),
                },
              },
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none',
              backgroundColor: alpha(currentPalette.background.paper, 0.8),
              backdropFilter: 'blur(20px)',
            },
          },
        },
        MuiIconButton: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
              '&:hover': {
                backgroundColor: alpha(currentPalette.divider, 0.3),
                transform: 'scale(1.05)',
              },
            },
          },
        },
      },
    }));
  }, [getThemeMode]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

export default ThemeConfig;
